import React, { useState } from 'react';
import { notificationService, adminService } from '../services/api';
import { formatDateTime } from '../utils/formatDateTime';
import { showSuccess, showError } from '../utils/toast';
import './NotificationsPanel.css';

/**
 * Central de Notificações (backend#306) — painel/dropdown aberto pelo sino
 * (envelope `mail`) do cabeçalho.
 *
 * O backend já filtra a visibilidade e devolve o estado de leitura por
 * usuário; este componente só consome `nao_lidas` + `notificacoes[].lida`.
 *
 * Props:
 *   notificacoes  — array de {id, titulo, corpo, tipo, created_at, lida}
 *   user          — objeto user atual (timezone + gate de super-admin)
 *   onClose       — fecha o painel
 *   onChanged     — callback após qualquer mutação (marcar lida / criar) pra
 *                   o header recarregar a lista e o badge a partir da fonte.
 */

// Rótulo + classe de cor por tipo. update = azul/info, issue = âmbar/alerta,
// info = neutro. Usa os tokens do design system (via NotificationsPanel.css).
const TIPO_META = {
  update: { label: 'Atualização', cls: 'notif-badge--update' },
  issue: { label: 'Aviso', cls: 'notif-badge--issue' },
  info: { label: 'Informação', cls: 'notif-badge--info' },
};

function NotificationsPanel({ notificacoes = [], user, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [criando, setCriando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [corpo, setCorpo] = useState('');
  const [tipo, setTipo] = useState('update');

  const isSuperadmin = !!user?.is_superadmin;
  const temNaoLidas = notificacoes.some((n) => !n.lida);

  const handleMarcarLida = async (id, jaLida) => {
    if (jaLida || busy) return;
    setBusy(true);
    try {
      await notificationService.marcarLida(id);
      if (typeof onChanged === 'function') await onChanged();
    } catch (err) {
      showError('Não foi possível marcar como lida. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  const handleMarcarTodas = async () => {
    if (busy || !temNaoLidas) return;
    setBusy(true);
    try {
      await notificationService.marcarTodasLidas();
      if (typeof onChanged === 'function') await onChanged();
    } catch (err) {
      showError('Não foi possível marcar todas como lidas. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  const handleCriar = async (e) => {
    e.preventDefault();
    const t = (titulo || '').trim();
    const c = (corpo || '').trim();
    if (!t) {
      showError('Informe um título.');
      return;
    }
    if (!c) {
      showError('Escreva o corpo da notificação.');
      return;
    }
    setBusy(true);
    try {
      await adminService.criarNotificacao({ titulo: t, corpo: c, tipo });
      showSuccess('Notificação publicada.');
      setTitulo('');
      setCorpo('');
      setTipo('update');
      setCriando(false);
      if (typeof onChanged === 'function') await onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showError(detail || 'Não foi possível publicar a notificação. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="notif-panel" role="dialog" aria-label="Notificações">
      <div className="notif-panel-header">
        <h3 className="notif-panel-title">Notificações</h3>
        <div className="notif-panel-actions">
          {temNaoLidas && (
            <button
              type="button"
              className="notif-link-btn"
              onClick={handleMarcarTodas}
              disabled={busy}
            >
              Marcar todas como lidas
            </button>
          )}
          <button
            type="button"
            className="notif-close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      </div>

      {isSuperadmin && (
        <div className="notif-admin">
          {!criando ? (
            <button
              type="button"
              className="notif-new-btn"
              onClick={() => setCriando(true)}
              disabled={busy}
            >
              <span className="material-symbols-outlined">add</span>
              Nova notificação
            </button>
          ) : (
            <form className="notif-form" onSubmit={handleCriar}>
              <label htmlFor="notif-titulo">Título</label>
              <input
                id="notif-titulo"
                type="text"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                disabled={busy}
                placeholder="Resumo do aviso"
                maxLength={200}
                autoFocus
              />

              <label htmlFor="notif-corpo">Corpo</label>
              <textarea
                id="notif-corpo"
                value={corpo}
                onChange={(e) => setCorpo(e.target.value)}
                disabled={busy}
                placeholder="Mensagem que aparecerá para os usuários"
                rows={4}
                maxLength={5000}
              />

              <label htmlFor="notif-tipo">Tipo</label>
              <select
                id="notif-tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                disabled={busy}
              >
                <option value="update">Atualização</option>
                <option value="issue">Aviso</option>
                <option value="info">Informação</option>
              </select>

              <div className="notif-form-actions">
                <button
                  type="button"
                  className="notif-link-btn"
                  onClick={() => setCriando(false)}
                  disabled={busy}
                >
                  Cancelar
                </button>
                <button type="submit" className="notif-btn-primary" disabled={busy}>
                  {busy ? 'Publicando...' : 'Publicar'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="notif-list">
        {notificacoes.length === 0 ? (
          <div className="notif-empty">
            <span className="material-symbols-outlined">notifications_off</span>
            <p>Sem notificações</p>
          </div>
        ) : (
          notificacoes.map((n) => {
            const meta = TIPO_META[n.tipo] || TIPO_META.info;
            return (
              <button
                key={n.id}
                type="button"
                className={`notif-item ${n.lida ? '' : 'notif-item--unread'}`}
                onClick={() => handleMarcarLida(n.id, n.lida)}
                disabled={busy}
                title={n.lida ? undefined : 'Marcar como lida'}
              >
                <div className="notif-item-top">
                  {!n.lida && <span className="notif-dot" aria-label="Não lida" />}
                  <span className={`notif-badge ${meta.cls}`}>{meta.label}</span>
                  <span className="notif-date">{formatDateTime(n.created_at, user)}</span>
                </div>
                <div className="notif-item-titulo">{n.titulo}</div>
                {n.corpo && <div className="notif-item-corpo">{n.corpo}</div>}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default NotificationsPanel;
